import {
  containerQueryConstants, Cookies, loadScriptSupplier, observeStore, setApiConfig, utils,
} from '@resy/ui-kitchen';
import { getApiConfig, postUserLocation } from '../../../ui-kitchen/src/api';
import translationMap from '../modules/translationMap';
import store from '../../../ui-kitchen/src/reducers/createStore';
import getConnectedActions from '../modules/actions/connectedActions';
import utmLastTouch from '../modules/utils/utmLastTouch';
import { getMktPageAnalytics, setGDAPageCookie } from '../modules/components/AmexMarketingUtils/AmexUtils';
import { getResyLocationsSelectedThunk, initResyLocationsConfigThunk } from '../../../ui-kitchen/src/reducers/resyLocationsSlice';
import {
  setAppContext, initCoreISODateThunk, initCoreSeatsMinMaxThunk, initCoreTimeThunk, initCoreSeatsThunk, setServiceConfig,
} from '../../../ui-kitchen/src/reducers/resyCoreSlice';
import { setSearchParams } from '../../../ui-kitchen/src/utils/searchParamUtils';
import APP_CONTEXT from '../../../ui-kitchen/src/constants/appContext';
import { CORE_SEATS_PARAM } from '../../../ui-kitchen/src/constants/resyCore';
import {
  getUserThunk,
  markReservationsChangedThunk,
  resetUserFetchState,
  selectIsGlobalDiningAccess,
  setShouldUserReservationsUpdate,
  setUserLocation,
  userLogoutThunk,
} from '../../../ui-kitchen/src/reducers/userSlice';
import { fetchRefreshToken, selectIsAuthenticated } from '../../../ui-kitchen/src/reducers/authTokenSlice';
import { initObserveStore } from '../../../ui-kitchen/src/utils';
import { isValidLatLong } from '../../../ui-kitchen/src/utils/userSliceUtils';
import ALLOWED_DOMAINS from '../../../ui-kitchen/src/constants/allowedDomains';
import isMaintenancePageEnabled, { fetchMaintenanceConfig } from '../../../ui-kitchen/src/utils/isMaintenancePageEnabled';
import applyAppBasePath from '../modules/utils/resolveAppBasePath';
import Logger from '../../../ui-kitchen/src/utils/logger';
import {
  AMEX_GDA_PAGE, AMEX_RESY_CREDIT, AMEX_RESY_VALUE, AMEX_GDA_DIRECTORY_PAGE, AMEX_PLATINUM_NIGHTS_PAGE,
} from '../../../ui-kitchen/src/constants/amexConstants';
import { IS_JCE_RESERVATION_NOTIFY_ENABLED } from '../../../ui-kitchen/src/constants/optimizelyExperiments';
import { setEssentialCookieAsync, setFunctionalCookie } from '../../../ui-kitchen/src/utils/resyUCMCookieUtils';
import { redirectToSanitizedUrl } from '../../../ui-kitchen/src/utils/sanitizeTargetUrl';

const { dispatch, getState } = store;
const {
  fetchCollections,
  fetchIpdata,
  getResyConfigThunk,
  loadResyConfigThunk,
  removeAuthToken,
  setAuthToken,
  updateMatchMedia,
} = getConnectedActions(dispatch);

const { checkIfType } = utils;

// used to cleanup ucm cookie listener
let deltaPageCookieCleanup = () => { };
let showSmsFlowCookieCleanup = () => { };
let GDACookieCleanup = () => { };

// Remote maintenance config fetched from S3. Cached at module scope so the
// store observer can re-evaluate the scheduled start window on every route.
let maintenanceConfig = null;

const JCE_COOKIE_NAME = 'jce_enabled';

// Per-section metadata for the consolidated `/account/:section/...` route.
// Applied via resolve on first match and re-applied on `$routeUpdate` when
// the URL changes within the route.
const ACCOUNT_SECTION_META = {
  profile: {
    titleKey: 'profile', descriptionKey: 'profile', pageName: 'Account Profile', bodyClasses: 'page-account',
  },
  lists: {
    titleKey: 'multiple_lists', pageName: 'Lists', bodyClasses: 'page-account', robots: 'noindex',
  },
  favorites: {
    titleKey: 'my_hit_list', pageName: 'Account Favorites', bodyClasses: 'page-account',
  },
  'payment-methods': {
    titleKey: 'payment_methods_title', pageName: 'Account Payment Methods', bodyClasses: 'page-account',
  },
  communication: {
    titleKey: 'communication_title', pageName: 'Account Communications', bodyClasses: 'page-account',
  },
  'help-and-feedback': {
    titleKey: 'help_and_feedback_title', pageName: 'Help and Feedback', bodyClasses: 'page-account',
  },
  'reservations-and-notify': {
    titleKey: 'reservations_and_notify', pageName: 'Account Reservations &amp; Notify', bodyClasses: 'page-account',
  },
  'concierge-reservations': {
    titleKey: 'concierge_reservations', pageName: 'Account Concierge Reservations', bodyClasses: 'page-account',
  },
};
const ACCOUNT_ROUTE_PATH = '/account/:section/:sub?/:subsub?';
const getAccountSectionFromPath = (path) => (path || '').split('/').filter(Boolean)[1];
const getAccountSectionMeta = (section) => ACCOUNT_SECTION_META[section] || ACCOUNT_SECTION_META.profile;
const isValidAccountSection = (section) => Object.prototype.hasOwnProperty.call(ACCOUNT_SECTION_META, section);
const getAccountMetadata = (section) => {
  const meta = getAccountSectionMeta(section);
  return {
    pageName: meta.pageName,
    bodyClasses: meta.bodyClasses,
    ...(meta.robots ? { robots: meta.robots } : {}),
  };
};

/* eslint-disable max-len, no-param-reassign, no-useless-escape */
angular.module('resyApp',
  [
    'ngAria',
    'ngRoute',
    'pascalprecht.translate',
    'ngRedux',
  ])
  .config(($compileProvider, $locationProvider, $ngReduxProvider, $routeProvider, $translateProvider, Config, AnnotationServiceConfig, AutomationMode, ResyosServiceConfig, SurveyServiceConfig, ItemsServiceConfig, REDIRECT_CONFIG, ResyAmexConfig, TockConfig) => {
    if (ENV === 'prod') {
      $compileProvider.debugInfoEnabled(false);
    }

    $ngReduxProvider.provideStore(store);

    // sets config values for api in ui-kitchen
    const ConfigCopy = { ...Config };
    if (AutomationMode) {
      ConfigCopy.apiKey = ConfigCopy.automationApiKey;
    }
    setApiConfig({
      Config: ConfigCopy,
      AnnotationServiceConfig,
      ResyosServiceConfig,
      ItemsServiceConfig,
      ResyAmexConfig,
      SurveyServiceConfig,
      TockConfig,
    });

    dispatch(setServiceConfig({ name: 'itemsService', config: ItemsServiceConfig }));
    dispatch(setServiceConfig({ name: 'annotationService', config: AnnotationServiceConfig }));
    dispatch(setServiceConfig({ name: 'resyosService', config: ResyosServiceConfig }));
    dispatch(setServiceConfig({ name: 'surveyService', config: SurveyServiceConfig }));
    dispatch(setServiceConfig({ name: 'tock', config: TockConfig }));

    // Correct the base tag before $location reads it, so a deploy served from
    // its /<version>/ URL routes there instead of at the zone root.
    applyAppBasePath();

    $locationProvider.html5Mode(true).hashPrefix('!');

    const isAuthenticated = async ($q, $rootScope, $location) => {
      const deferred = $q.defer();
      const hasRefreshToken = await dispatch(fetchRefreshToken('Consumer'));
      if (selectIsAuthenticated(getState()) || hasRefreshToken) {
        deferred.resolve();
      } else {
        $rootScope.nav.backPath = $location.path();
        deferred.reject();
      }

      return deferred.promise;
    };

    const redirectToBlendWhenAuthenticated = async ($q, $rootScope, $location, inviteId) => {
      const deferred = $q.defer();
      const hasRefreshToken = await dispatch(fetchRefreshToken('Consumer'));
      const amexBlendUrl = inviteId
        ? `/auth-blend/?inviteId=${encodeURIComponent(inviteId)}`
        : '/auth-blend/';
      const internalBlendUrl = inviteId
        ? `/blend/i/inviteId=${encodeURIComponent(inviteId)}`
        : '/blend/';

      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (!isMobile) {
        $location.path('/blend/error');
        return deferred.resolve();
      }

      if (selectIsAuthenticated(getState()) || hasRefreshToken) {
        window.location.href = amexBlendUrl;
        deferred.resolve();
      } else {
        $rootScope.nav.backPath = internalBlendUrl;
        deferred.reject();
      }

      return deferred.promise;
    };

    const validateForOnboarding = async ($location, $rootScope, $q, $route) => {
      const deferred = $q.defer();

      if ($route.current.params.key === Config.apiKey) {
        deferred.resolve();
      } else {
        $rootScope.nav.backPath = $location.path();
        deferred.reject();
      }

      return deferred.promise;
    };

    const setDeltaWifiCookie = () => {
      deltaPageCookieCleanup = setFunctionalCookie('_deltaPage_', 'Y');
    };

    const platinumNightsRouteParams = {
      template: '<resy-router></resy-router>',
      reloadOnSearch: false,
      resolve: {
        showTopNav: () => false,
        showNav: () => false,
        showFooter: () => true,
        metadata: () => ({
          bodyClasses: 'page-platinum-nights',
          pageName: AMEX_PLATINUM_NIGHTS_PAGE,
        }),
        titleKey: () => 'platinum_nights',
        descriptionKey: () => 'platinum_nights',
      },
    };

    const resyCreditCardRouteParams = {
      template: '<resy-router></resy-router>',
      reloadOnSearch: false,
      resolve: {
        showTopNav: () => false,
        showNav: () => false,
        showFooter: () => true,
        metadata: () => ({
          robots: 'noindex',
          bodyClasses: 'page-resy-credit',
          pageName: AMEX_RESY_CREDIT,
        }),
        titleKey: () => 'resy_credit',
        descriptionKey: () => 'resy_credit',
      },
    };
    $routeProvider
      .when('/', {
        template: '<resy-home></resy-home>',
        reloadOnSearch: false,
        resolve: {
          metadata: () => ({
            pageName: 'Home',
            bodyClasses: 'page-home',
          }),
          titleKey: () => 'home',
          hideSelectors: () => false,
        },
      })
      .when('/cancel/:cancelToken', {
        template: '<resy-home></resy-home>',
        reloadOnSearch: false,
      })
      .when('/blend', {
        template: '<div class="loading"></div>',
        metadata: () => ({
          robots: 'noindex',
        }),
        resolve: {
          auth: ($q, $rootScope, $location) => redirectToBlendWhenAuthenticated($q, $rootScope, $location),
        },
      })
      .when('/blend/i/inviteId=:inviteId', {
        template: '<div class="loading"></div>',
        metadata: () => ({
          robots: 'noindex',
        }),
        resolve: {
          auth: ($q, $rootScope, $location, $route) => redirectToBlendWhenAuthenticated(
            $q,
            $rootScope,
            $location,
            $route.current.params.inviteId
          ),
        },
      })
      .when('/blend/error', {
        template: '<resy-blend-error></resy-blend-error>',
        resolve: {
          metadata: () => ({
            robots: 'noindex',
          }),
          prerender: () => ({ responseCode: 403 }),
          titleKey: () => 'blend_error',
        },
      })
      .when('/list-venues', {
        template: '<resy-list-venues metadata="$resolve.metadata"></resy-list-venues>',
        resolve: {
          metadata: () => ({
            pageName: 'List Venues',
            bodyClasses: 'page-venue-listing',
          }),
          titleKey: () => 'all_restaurants',
        },
      })

      .when('/cities/:location', {
        template: '<resy-home></resy-home>',
        reloadOnSearch: false,
        resolve: {
          metadata: () => ({
            pageName: 'Home',
            bodyClasses: 'page-home',
          }),
          titleKey: () => 'home',
          hideSelectors: () => false,
        },
      })

      .when('/cities/:location/search', {
        template: '<resy-search></resy-search>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showFooter: () => false,
          titleKey: () => 'search_restaurants',
          metadata: ($routeParams) => ({
            pageName: 'Venue Listings',
            bodyClasses: `page-search ${$routeParams.location}`,
          }),
        },
      })

      .when('/cities/:location/list/:list', {
        template: '<resy-search></resy-search>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showFooter: () => false,
          titleKey: () => 'search_restaurants',
          metadata: ($routeParams) => ({
            pageName: 'Venue Listings',
            bodyClasses: `page-search ${$routeParams.location}`,
          }),
        },
      })

      .when('/cities/:city/events', {
        templateUrl: 'views/eventListing.html',
        controller: 'EventListingCtrl',
        controllerAs: 'PageCtrl',
        reloadOnSearch: false,
        resolve: {
          titleKey: () => 'events',
          descriptionKey: () => 'events',
          metadata: () => ({
            pageName: 'Event Listing',
            bodyClasses: 'page-venue-listing',
          }),
          hideSelectors: () => false,
        },
      })
      .when('/cities/:location/venues/:venueSlug', {
        templateUrl: 'views/venueDetails.html',
        controller: 'VenueDetailsCtrl',
        controllerAs: 'PageCtrl',
        reloadOnSearch: false,
        resolve: {
          titleKey: () => 'restaurant_reservations',
          hideGlobalSelectors: () => true,
          metadata: () => ({
            pageName: 'Venue Details',
            bodyClasses: 'page-venue-details',
          }),
        },
      })
      .when('/cities/:location/:venueSlug/:shopsmall', {
        templateUrl: 'views/venueDetails.html',
        controller: 'VenueDetailsCtrl',
        controllerAs: 'PageCtrl',
        reloadOnSearch: false,
        resolve: {
          titleKey: () => 'restaurant_reservations',
          hideGlobalSelectors: () => true,
          metadata: () => ({
            pageName: 'Venue Details',
            bodyClasses: 'page-venue-details',
          }),
        },
      })

      // Event Details Page - Preview
      .when('/cities/:location/venues/:venueSlug/events/:eventSlug/preview/:previewToken', {
        template: '<event-details-container select-resy="PageCtrl.selectResy" preview-mode=true></event-details-container>',
        controller: 'EventDetailsCtrl',
        controllerAs: 'PageCtrl',
        resolve: {
          prerender: () => ({ responseCode: 404 }),
          previewMode: () => true,
          metadata: () => ({
            pageName: 'Event Preview',
            robots: 'noindex',
          }),
          hideSelectors: () => false,
        },
      })

      // Event Details Page
      .when('/cities/:location/venues/:venueSlug/events/:eventSlug', {
        template: `
          <event-details-container
            on-ticket-quantity-select="PageCtrl.onTicketQuantitySelect"
            login="PageCtrl.showAuthModal"
            seats="PageCtrl.selectedQuantity"
            select-resy="PageCtrl.selectResy"
            user-is-gda="PageCtrl.userIsGda"
          ></event-details-container>
        `,
        controller: 'EventDetailsCtrl',
        controllerAs: 'PageCtrl',
        reloadOnSearch: false,
        resolve: {
          prerender: () => ({ responseCode: 404 }),
          metadata: () => ({
            pageName: 'Event Details',
            bodyClasses: 'page-event-details',
          }),
          hideSelectors: () => false,
        },
      })

      .when('/i/:token', { // short url for text
        redirectTo: '/invite/rsvp/:token',
      })
      .when('/invite/rsvp/:token', {
        template: '<resy-invite-rsvp></resy-invite-rsvp>',
        resolve: {
          prerender: () => ({ responseCode: 404 }),
          titleKey: () => 'invite',
          metadata: () => ({
            pageName: 'Invite RSVP',
            bodyClasses: 'page-invite-rsvp page-invite',
          }),
        },
      })
      .when('/invite/decline/:token', {
        template: '<resy-invite-rsvp decline="true"></resy-invite-rsvp>',
        resolve: {
          prerender: () => ({ responseCode: 404 }),
          titleKey: () => 'invite.declined',
          metadata: () => ({
            pageName: 'Invite Decline',
            bodyClasses: 'page-invite-rsvp page-invite',
          }),
        },
      })

      .when('/survey/:urlCode', {
        template: '<resy-router></resy-router>',
        resolve: {
          metadata: () => ({
            pageName: 'Survey',
          }),
        },
      })

      .when('/user/profile/rating/opt_out', {
        template: '<opt-out></opt-out>',
        resolve: {
          metadata: () => ({
            pageName: 'Rating Opt-Out',
          }),
        },
      })

      .when('/prepay/:token', {
        template: '<prepay-start></prepay-start>',
        resolve: {
          prerender: () => ({ responseCode: 404 }),
          titleKey: () => 'prepay',
          metadata: () => ({
            pageName: 'Prepay',
          }),
        },
      })
      .when('/prepay/:token/payment-method-stripe', {
        template: '<prepay-payment></prepay-payment>',
        resolve: {
          prerender: () => ({ responseCode: 404 }),
          titleKey: () => 'prepay_payment_method',
          metadata: () => ({
            pageName: 'Prepay Payment Method',
          }),
        },
      })
      .when('/prepay/:token/confirmation', {
        template: '<prepay-confirmation></prepay-confirmation>',
        resolve: {
          prerender: () => ({ responseCode: 404 }),
          titleKey: () => 'prepay_confirmation',
          metadata: () => ({
            pageName: 'Prepay Confirmation',
          }),
        },
      })

      .when('/rate', {
        templateUrl: 'views/ratings.html',
        controller: 'RatingsCtrl',
        controllerAs: 'PageCtrl',
        resolve: {
          prerender: () => ({ responseCode: 404 }),
          titleKey: () => 'rate',
          metadata: () => ({
            pageName: 'Ratings',
            bodyClasses: 'page-ratings',
          }),
          hideSelectors: () => true,
        },
      })

      /** ****************************************************************
      *          Account Pages Start
      ***************************************************************** */
      .when('/concierge', {
        redirectTo: '/account/profile?concierge=1',
      })
      .when('/account/reservations', {
        redirectTo: '/account/reservations-and-notify',
      })
      .when('/shared/lists/:guid', {
        template: '<resy-router></resy-router>',
        resolve: {
          shouldNavWatchScroll: () => false,
          titleKey: () => 'multiple_lists',
          metadata: () => ({
            pageName: 'Lists',
            bodyClasses: 'page-share-list',
            robots: 'noindex',
          }),
          hideSelectors: () => true,
        },
      })
      // Consolidated route for every React-router-managed account section.
      // `reloadOnUrl: false` keeps `<resy-router>` mounted across sidenav
      // clicks so the React tree (and the GAM ad slot) survives section
      // transitions. React Router's nested <Outlet>s handle per-section UI.
      .when(ACCOUNT_ROUTE_PATH, {
        template: '<resy-router></resy-router>',
        reloadOnUrl: false,
        resolve: {
          sectionGuard: ($location) => {
            const section = getAccountSectionFromPath($location.path());
            if (!isValidAccountSection(section)) {
              $location.path('/404');
            }
          },
          auth: ($q, $rootScope, $location) => isAuthenticated($q, $rootScope, $location),
          prerender: () => ({ responseCode: 404 }),
          shouldNavWatchScroll: () => false,
          hideSelectors: () => true,
          titleKey: ($location) => getAccountSectionMeta(getAccountSectionFromPath($location.path())).titleKey,
          descriptionKey: ($location) => getAccountSectionMeta(getAccountSectionFromPath($location.path())).descriptionKey,
          metadata: ($location) => getAccountMetadata(getAccountSectionFromPath($location.path())),
        },
      })
      /** ****************************************************************
       *          Account Pages End
       ***************************************************************** */

      /** ****************************************************************
       *          Static Pages Start
      ***************************************************************** */
      .when('/about', {
        redirectTo: '/about-and-help',
      })
      .when('/about-and-help', {
        template: '<resy-router></resy-router>',
        resolve: {
          metadata: () => ({
            bodyClasses: 'page-about page-static',
            pageName: 'About',
          }),
          shouldNavWatchScroll: () => false,
          titleKey: () => 'about',
          descriptionKey: () => 'about',
          hideSelectors: () => true,
        },
      })
      .when('/welcome', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => true,
          metadata: () => ({
            bodyClasses: 'page-resy-value',
            pageName: AMEX_RESY_VALUE,
          }),
          titleKey: () => 'resy_value',
          descriptionKey: () => 'resy_value',
        },
      })
      .when('/welcomecardmember', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => true,
          metadata: () => ({
            bodyClasses: 'page-resy-value',
            pageName: AMEX_RESY_VALUE,
          }),
          titleKey: () => 'resy_value',
          descriptionKey: () => 'resy_value',
        },
      })
      .when('/global-dining-access', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => false,
          metadata: () => ({
            bodyClasses: 'page-amex-gda',
            pageName: AMEX_GDA_PAGE,
          }),
          titleKey: () => 'amex_gda',
          descriptionKey: () => 'amex_gda',
        },
      })
      .when('/global-dining-access-member', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => false,
          metadata: () => ({
            robots: 'noindex',
            bodyClasses: 'page-amex-gda',
            pageName: 'Amex GDA',
          }),
          titleKey: () => 'amex_gda',
          descriptionKey: () => 'amex_gda',
        },
      })
      .when('/global-dining-access/directory', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => false,
          metadata: () => ({
            bodyClasses: 'page-amex-gdadir',
            pageName: AMEX_GDA_DIRECTORY_PAGE,
          }),
          titleKey: () => 'amex_gda_dir',
          descriptionKey: () => 'amex_gda_dir',
        },
      })
      .when('/delta', {
        template: '<resy-amex active="\'delta-wifi\'"></resy-amex>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => false,
          metadata: () => ({
            bodyClasses: 'page-delta-wifi',
            pageName: 'Delta Wifi Agnostic',
            robots: 'noindex',
          }),
          titleKey: () => 'delta_wifi',
          descriptionKey: () => 'delta_wifi',
          setDeltaCookie: () => (setDeltaWifiCookie()),
        },
      })
      .when('/delta/:city', {
        template: '<resy-amex active="\'delta-wifi\'"></resy-amex>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => false,
          metadata: () => ({
            bodyClasses: 'page-delta-wifi',
            pageName: 'Delta Wifi',
            robots: 'noindex',
          }),
          titleKey: () => 'delta_wifi',
          descriptionKey: () => 'delta_wifi',
          setDeltaCookie: () => (setDeltaWifiCookie()),
        },
      })
      .when('/cnyevents', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => false,
          metadata: () => ({
            bodyClasses: 'page-amex-centurion-ny-events',
            pageName: 'Centurion New York Events',
            robots: 'noindex',
          }),
          titleKey: () => 'amex_centurion_ny_events',
          descriptionKey: () => 'amex_centurion_ny_events',
        },
      })
      .when('/dynamicterms/:initiativeid', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => true,
          metadata: () => ({
            bodyClasses: 'page-amex-dynamic-terms',
            pageName: 'Amex Dynamic Terms',
            robots: 'noindex',
          }),
          titleKey: () => 'amex_dynamic_terms',
          descriptionKey: () => 'amex_dynamic_terms',
        },
      })
      .when('/nps2022q4terms', { // nps2022q4terms
        redirectTo: '/dynamicterms/nps2022q4terms',
      })
      .when('/nps2023q2terms', { // nps2023q2terms
        redirectTo: '/dynamicterms/nps2023q2terms',
      })
      .when('/shopsmall/', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => true,
          metadata: () => ({
            bodyClasses: 'page-amex-shop-small',
            pageName: 'Shop Small',
          }),
          titleKey: () => 'amex_shop_small',
          descriptionKey: () => 'amex_shop_small',
        },
      })
      .when('/shop-small', { // short url for marketing team
        redirectTo: '/shopsmall',
      })
      .when('/security-center', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => true,
          metadata: () => ({
            bodyClasses: 'page-resy-security-center',
            pageName: 'Security Center',
          }),
          titleKey: () => 'resy_security_center',
          descriptionKey: () => 'resy_security_center',
        },
      })
      .when('/resy-rewards', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => true,
          metadata: () => ({
            bodyClasses: 'page-resy-rewards',
            pageName: 'Resy Rewards',
          }),
          titleKey: () => 'resy_rewards',
          descriptionKey: () => 'resy_rewards',
        },
      })
      .when('/resycredit', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => true,
          metadata: () => ({
            bodyClasses: 'page-resy-credit',
            pageName: AMEX_RESY_CREDIT,
          }),
          titleKey: () => 'resy_credit',
          descriptionKey: () => 'resy_credit',
        },
      })
      .when('/resycredit/amexgold', resyCreditCardRouteParams)
      .when('/resycredit/deltaskymilesreserve', resyCreditCardRouteParams)
      .when('/resycredit/deltaskymilesplatinum', resyCreditCardRouteParams)
      .when('/resycredit/platinumcard', resyCreditCardRouteParams)
      .when('/experiences', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => true,
          metadata: () => ({
            robots: 'noindex',
            bodyClasses: 'page-amex-experiences',
            pageName: 'Experiences',
          }),
          titleKey: () => 'amex_experiences',
          descriptionKey: () => 'amex_experiences',
        },
      })
      .when('/global-dining-access-partner-page', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => false,
          metadata: () => ({
            bodyClasses: 'page-amex-gda-b2b',
            pageName: 'GDA B2B',
          }),
          titleKey: () => 'amex_gda_b2b',
          descriptionKey: () => 'amex_gda_b2b',
        },
      })
      .when('/specialaccess', platinumNightsRouteParams)
      .when('/platinum', platinumNightsRouteParams)
      .when('/platinum-member', platinumNightsRouteParams)
      .when('/amex-platinum', platinumNightsRouteParams)
      .when('/global-dining-access-onboarding/:key', {
        template: '<resy-router></resy-router>',
        resolve: {
          checkApiKey: ($location, $rootScope, $q, $route) => validateForOnboarding($location, $rootScope, $q, $route),
          prerender: () => ({ responseCode: 404 }),
          showModals: () => false,
          showTopNav: () => false,
          showNav: () => false,
          showFooter: () => false,
          metadata: () => ({
            robots: 'noindex',
            bodyClasses: 'page-full-bleed',
            pageName: 'Global Dining Access Onboarding',
          }),
        },
      })
      .when('/careers', {
        template: '<resy-router></resy-router>',
        resolve: {
          metadata: () => ({
            bodyClasses: 'page-careers page-static',
            pageName: 'Work at Resy',
          }),
          shouldNavWatchScroll: () => false,
          titleKey: () => 'careers',
          hideSelectors: () => true,
        },
      })
      .when('/terms/resypay', {
        template: '<resy-router></resy-router>',
        resolve: {
          metadata: () => ({
            bodyClasses: 'page-terms page-static',
            pageName: 'Terms of Service - ResyPay',
          }),
          shouldNavWatchScroll: () => false,
          titleKey: () => 'terms-resypay',
          hideSelectors: () => true,
        },
      })
      .when('/terms', {
        template: '<resy-router></resy-router>',
        resolve: {
          metadata: () => ({
            bodyClasses: 'page-terms page-static',
            pageName: 'Terms of Service',
          }),
          shouldNavWatchScroll: () => false,
          titleKey: () => 'terms',
          hideSelectors: () => true,
        },
      })
      .when('/privacy', {
        template: '<resy-router></resy-router>',
        reloadOnSearch: false,
        resolve: {
          metadata: () => ({
            bodyClasses: 'page-privacy page-static',
            pageName: 'Privacy Policy',
          }),
          shouldNavWatchScroll: () => false,
          titleKey: () => 'privacy',
          hideSelectors: () => true,
        },
      })
      .when('/cookie-policy', {
        template: '<resy-router></resy-router>',
        resolve: {
          metadata: () => ({
            bodyClasses: 'page-cookie page-static',
            pageName: 'Cookie Policy',
          }),
          shouldNavWatchScroll: () => false,
          titleKey: () => 'cookie',
          hideSelectors: () => true,
        },
      })

      .when('/dpa', {
        template: '<resy-router></resy-router>',
        resolve: {
          metadata: () => ({
            bodyClasses: 'page-dpa page-static',
            pageName: 'Data Processing Addendum',
          }),
          shouldNavWatchScroll: () => false,
          titleKey: () => 'dpa',
          hideSelectors: () => true,
        },
      })

      .when('/accessibility-statement', {
        template: '<resy-router></resy-router>',
        resolve: {
          metadata: () => ({
            bodyClasses: 'page-accessibility page-static',
            pageName: 'Resy Accessibility Statement',
          }),
          shouldNavWatchScroll: () => false,
          titleKey: () => 'accessibility',
          descriptionKey: () => 'accessibility',
          hideSelectors: () => true,
        },
      })
      /** ****************************************************************
       *          Static Pages End
       ***************************************************************** */
      .when('/password', { redirectTo: '/reset-password' })
      .when('/reset-password/:key?', {
        template: '<resy-reset-password-container></resy-reset-password-container>',
        resolve: {
          metadata: () => ({
            pageName: 'Reset Password',
          }),
          prerender: () => ({ responseCode: 404 }),
          titleKey: () => 'reset_password',
        },
      })
      .when('/email-verified', {
        template: '<resy-email-verified></resy-email-verified>',
        resolve: {
          metadata: () => ({
            pageName: 'Email Verified',
          }),
          prerender: () => ({ responseCode: 404 }),
          titleKey: () => 'email_verified',
        },
      })
      .when('/download', {
        template: '<div class="loading"></div>',
        controller: 'DownloadCtrl',
        resolve: {
          metadata: () => ({
            pageName: 'Download',
          }),
          prerender: () => ({ responseCode: 404 }),
          titleKey: () => 'download',
        },
      })
      .when('/link', {
        template: '<div class="loading"></div>',
        controller: 'LinkCtrl',
        resolve: {
          metadata: () => ({
            pageName: 'Link',
          }),
          titleKey: () => 'link',
        },
      })
      .when('/resyos/install', {
        template: '<install-redirect></install-redirect>',
      });

    /** ****************************************************************
     *          Blog Redirects Start
     ***************************************************************** */
    REDIRECT_CONFIG.routes.forEach((route) => {
      $routeProvider.when(`/${route}`, {
        template: '<div class="loading"></div>',
        controller: 'RedirectCtrl',
        caseInsensitiveMatch: true,
        resolve: {
          prerender: () => {
            const path = route.toLowerCase().replace(/-/g, '');
            return {
              responseCode: 301,
              location: `Location: ${REDIRECT_CONFIG.map[path]}`,
            };
          },
        },
      });
    });
    /** ****************************************************************
     *          Blog Redirects End
     ***************************************************************** */

    $routeProvider
      .when('/404', {
        template: '<resy-error></resy-error>',
        resolve: {
          prerender: () => ({ responseCode: 404 }),
          titleKey: () => 'error',
        },
      })
      .otherwise({
        redirectTo: '/404',
      });

    window.translationMap = translationMap;

    $translateProvider
      .translations('en-US', window.translationMap)
      .useLoader('i18nLoader', {
        prefix: '../locale/strings/',
        suffix: '.json',
      })
      .registerAvailableLanguageKeys(['ca-ES', 'en-US', 'es-ES', 'fr-FR', 'fr-CA', 'ja-JP', 'nl-NL', 'de-DE', 'pt-BR', 'pt-PT'], {
        'ca*': 'ca-ES',
        'en*': 'en-US',
        'es*': 'es-ES',
        'ja*': 'ja-JP',
        'fr*': 'fr-FR',
        'nl*': 'nl-NL',
        'de*': 'de-DE',
        'pt*': 'pt-PT',
        '*': 'en-US',
      })
      .uniformLanguageTag('bcp47')
      .determinePreferredLanguage()
      .fallbackLanguage('en-US')
      .useSanitizeValueStrategy(null);
  })
  .run(($location, $rootScope, $route, $routeParams, $translate, $window, Analytics, Config, $document) => {
    let initLocations = false;
    let initIpdata = false;
    let initMediaQueries = false;
    let thisSelectedCoreISODate;
    let thisSelectedCoreSeats;
    let Collections = {};
    const AuthToken = {};

    $rootScope.reduxStore = store;
    dispatch(setAppContext(APP_CONTEXT.CONSUMER));
    dispatch(initCoreISODateThunk());
    dispatch(initCoreSeatsThunk());
    dispatch(initCoreTimeThunk());
    loadResyConfigThunk()
      .then(() => {
        dispatch(initCoreSeatsMinMaxThunk());
        if (selectIsAuthenticated(getState())) {
          dispatch(getUserThunk());
        }
      });

    const getCoords = (coords) => {
      if (coords.lat && coords.long) {
        const { lat, long } = coords;
        return { lat, long };
      }
      // ny
      return {
        lat: 40.7643304,
        long: -73.9772046,
      };
    };

    const shouldShowMaintenance = () => isMaintenancePageEnabled(maintenanceConfig);

    const onStoreChange = (selectedState) => {
      Collections = selectedState.collections;

      if (!AuthToken.set) {
        AuthToken.set = setAuthToken;
      }
      if (!initIpdata) {
        initIpdata = true;
        fetchIpdata();
      }
      if (selectedState.ipdata.loaded && selectedState.experiments.loaded && !selectedState.ipdata.loadedFromCookie && !document.getElementById('script-supplier')) {
        const { countryCode, success } = getState().ipdata;
        // Default to GB (consent market) if geoip success is false
        const country = success ? countryCode : 'GB';
        const geoFlag = getState().experiments.ads_web_geo_enabled_country;
        const allowedCountries = (geoFlag?.enabled && geoFlag?.variables?.json_variable?.enabled_countries) ?? [];
        loadScriptSupplier(country, allowedCountries);
      }

      if (selectedState.ipdata.loaded && !initLocations) {
        initLocations = true;
        dispatch(setUserLocation(selectedState.ipdata));
        if (selectIsAuthenticated(getState())) {
          if (isValidLatLong(selectedState.ipdata)) {
            postUserLocation({
              x: selectedState.ipdata.long,
              y: selectedState.ipdata.lat,
            });
          }
        }
        const params = {
          coordinates: getCoords(selectedState.ipdata),
          $location,
          $rootScope,
          $route,
          $routeParams,
          $window,
        };
        dispatch(initResyLocationsConfigThunk(params));
      }

      const location = dispatch(getResyLocationsSelectedThunk());
      if ($rootScope.locationCode !== location?.code) {
        $rootScope.locationCode = location?.code;
        loadCollections();
      }
      const key = `${location?.code}-1-${selectIsAuthenticated(getState())}`;
      if (selectedState.collections?.lists?.all[key]) {
        $rootScope.collections = selectedState.collections?.lists?.all[key];
      }
      if (!initMediaQueries) {
        initMediaQueries = true;
        $rootScope.matchMedia = selectedState.matchMedia;
        utils.initMediaQueryListeners(updateMatchMedia, containerQueryConstants);
      } else if ($rootScope.matchMedia !== selectedState.matchMedia) {
        $rootScope.$apply(() => {
          $rootScope.matchMedia = selectedState.matchMedia;
        });
      }
      // Show the full-page maintenance ("service down") screen whenever the
      // remote maintenance flag is enabled and we're inside the scheduled window.
      const maintenanceEnabled = shouldShowMaintenance();
      if ($rootScope.showMaintenance !== maintenanceEnabled) {
        $rootScope.$applyAsync(() => {
          $rootScope.showMaintenance = maintenanceEnabled;
        });
      }

      const { selectedCoreISODate, selectedCoreSeats } = selectedState.resyCore;
      if (thisSelectedCoreISODate !== selectedCoreISODate) {
        thisSelectedCoreISODate = selectedCoreISODate;
      }
      if (thisSelectedCoreSeats !== selectedCoreSeats) {
        thisSelectedCoreSeats = selectedCoreSeats;
      }
    };

    const unsubscribeStore = observeStore(
      store,
      (state) => (state),
      onStoreChange
    );

    let prevUserData = null;
    let authBootstrapComplete = false;
    const onToggleUser = () => {
      $route.reload();
      loadCollections();
    };
    const onUserSliceChange = (userSlice) => {
      const { userData } = userSlice;

      // Wait for refresh-token bootstrap before processing auth changes
      if (!authBootstrapComplete) {
        prevUserData = userData;
        return;
      }

      // Angular 'UserAuth' event handling
      if (prevUserData === null && userData) {
        onToggleUser();
      }
      // Angular 'UserLogout' event handling
      if (prevUserData && userData === null) {
        onToggleUser();
      }
      prevUserData = userData;
    };

    const observeUserSlice = initObserveStore(store, ({ user }) => (user));
    const unsubscribeUserSlice = observeUserSlice(onUserSliceChange);

    dispatch(fetchRefreshToken('Consumer'))
      .finally(() => {
        authBootstrapComplete = true;
      });

    const removeCookieListeners = () => {
      deltaPageCookieCleanup();
      showSmsFlowCookieCleanup();
      GDACookieCleanup();
    };

    $rootScope.$on('$destroy', () => {
      unsubscribeStore();
      unsubscribeUserSlice();
      removeCookieListeners();
    });

    /* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["$rootScope", "$window"] }] */
    let isWatchingScroll = false;
    $rootScope.scrollTo = scrollTo;
    $rootScope.isScrolled = false;
    $rootScope.metadata = {};
    $rootScope.collections = {};
    $rootScope.prerender = {};
    $rootScope.showModals = true;
    $rootScope.showTopNav = false;
    $rootScope.showNav = false;
    $rootScope.showFooter = false;
    $rootScope.showMaintenance = false;

    // Pull the remote maintenance config once on load, from the bucket that
    // matches the build environment. The store observer keeps `showMaintenance`
    // in sync as the scheduled window opens and closes, and
    // `$rootScope.maintenance` feeds the restore-time copy on the page.
    fetchMaintenanceConfig(Config.assetsBaseUrl).then((config) => {
      maintenanceConfig = config;
      $rootScope.$applyAsync(() => {
        $rootScope.maintenance = config;
        $rootScope.showMaintenance = shouldShowMaintenance();
      });
    });

    setTitle();
    assessLanguage();
    assessBrowser();
    setUpFlags();
    watchScroll();

    $window.resyWidget = resyWidget;

    $rootScope.nav = {
      backPath: null,
    };

    $rootScope.hasLocalStorageSupport = 'localStorage' in $window;

    if ($rootScope.hasLocalStorageSupport) {
      const testKey = 'resy.translate.storageTest';

      try {
        // this check have to be wrapped within a try/catch because on
        // a SecurityError: Dom Exception 18 on iOS
        if ($window.localStorage !== null) {
          $window.localStorage.setItem(testKey, 'foo');
          $window.localStorage.removeItem(testKey);
          $rootScope.hasLocalStorageSupport = true;
        } else {
          $rootScope.hasLocalStorageSupport = false;
        }
      } catch (e) {
        $rootScope.hasLocalStorageSupport = false;
      }
    }

    $rootScope.$on('$routeChangeSuccess', (e, current, previous) => {
      /**
       * $route.reload will trigger $routeChangeSuccess
       * This will assess if a reload has occurred so we don't run certain logic in that case.
       * Otherwise, for example, the widget modal may be inadvertently closed.
       */
      let shouldExecute = true;
      if (previous && !checkIfType.isUndefined(previous.loadedTemplateUrl) && previous.loadedTemplateUrl === current.loadedTemplateUrl) {
        shouldExecute = false;
      }

      if (shouldExecute) {
        setTitle();

        if (!checkIfType.isUndefined(current.locals.titleKey)) {
          $translate(`titles.${current.locals.titleKey}`)
            .then(
              (translationString) => {
                $rootScope.metadata.title = `${translationString}`;
              },
              () => {
                $rootScope.metadata.title = $rootScope.titleBase;
              }
            );
        } else {
          $rootScope.metadata.title = $rootScope.titleBase;
        }

        if (!checkIfType.isUndefined(current.locals.descriptionKey)) {
          $translate(`descriptions.${current.locals.descriptionKey}`)
            .then(
              (translationString) => {
                $rootScope.metadata.description = `${translationString}`;
              },
              () => {
                $rootScope.metadata.description = $rootScope.descriptionBase;
              }
            );
        } else {
          $rootScope.metadata.description = $rootScope.descriptionBase;
        }

        if (checkIfType.isUndefined(current.locals.shouldNavWatchScroll) || current.locals.shouldNavWatchScroll) {
          if (!isWatchingScroll) {
            $window.addEventListener('scroll', onScroll);
            isWatchingScroll = true;
          }
        } else {
          const bodyClasses = current.locals?.metadata?.bodyClasses || '';
          // account or static
          if (bodyClasses.includes('page-account') || bodyClasses.includes('page-static')) {
            // only remove if sm or md
            if (!$rootScope.matchMedia.lg) {
              $window.removeEventListener('scroll', onScroll);
              isWatchingScroll = false;
              $rootScope.isScrolled = false;
            } else if (!isWatchingScroll) {
              $window.addEventListener('scroll', onScroll);
              isWatchingScroll = true;
            }
          } else {
            $window.removeEventListener('scroll', onScroll);
            isWatchingScroll = false;
            $rootScope.isScrolled = false;
          }
        }

        if (!checkIfType.isUndefined(current.locals.showTopNav)) {
          $rootScope.showTopNav = current.locals.showTopNav;
        } else {
          $rootScope.showTopNav = true;
        }

        if (!checkIfType.isUndefined(current.locals.showModals)) {
          $rootScope.showModals = current.locals.showModals;
        } else {
          $rootScope.showModals = true;
        }

        /**
         * This was originally implemented to support the Caviar Contest page,
         * which needed to hide the global nav.
         *
         * Removing the Caviar Contest page so there is technically nothing using this
         * functionality, but I will leave it in case we need to do this in the future
         * - MY
         */
        if (!checkIfType.isUndefined(current.locals.showNav)) {
          $rootScope.showNav = current.locals.showNav;
        } else {
          $rootScope.showNav = true;
        }

        // hide selectors on Event Details page
        if (!checkIfType.isUndefined(current.locals.hideSelectors)) {
          $rootScope.hideSelectors = current.locals.hideSelectors;
        } else {
          $rootScope.hideSelectors = false;
        }

        if (current.locals.titleKey === 'search_restaurants' && $rootScope.matchMedia.md) {
          $rootScope.hideSelectors = true;
          $rootScope.searchPageSelectors = true;
        } else {
          $rootScope.searchPageSelectors = false;
        }

        if (current.locals.hideGlobalSelectors) {
          $rootScope.hideSelectors = true;
        }

        // disable header and footer links for Event Preview page
        if (!checkIfType.isUndefined(current.locals.previewMode)) {
          $rootScope.previewMode = current.locals.previewMode;
        } else {
          $rootScope.previewMode = false;
        }

        // Hiding footer should only occur on search page
        if (!checkIfType.isUndefined(current.locals.showFooter)) {
          $rootScope.showFooter = current.locals.showFooter;
        } else {
          $rootScope.showFooter = true;
        }

        // Tradedesk universal pixel for B2C marketing
        /* eslint-disable no-undef, camelcase */
        if (Analytics.tddInit) {
          Analytics.tddInit(
            '3koigrx',
            ['qrxyf29'],
            'https://insight.adsrvr.org/track/up',
            null
          );
        }
        /* eslint-enable */

        if (!checkIfType.isUndefined(current.locals.prerender) && !checkIfType.isUndefined(current.locals.prerender.responseCode)) {
          $rootScope.prerender.responseCode = current.locals.prerender.responseCode;

          if (!checkIfType.isUndefined(current.locals.prerender.location)) {
            $rootScope.prerender.location = current.locals.prerender.location;
          } else {
            $rootScope.prerender.location = null;
          }
        } else {
          $rootScope.prerender.responseCode = 200;
        }

        if (current?.$$route.originalPath !== previous?.$$route.originalPath) {
          resyWidget.closeModal();
        }

        $rootScope.$evalAsync(() => {
          $window.scrollTo(0, 0);
        });
      }

      // set metadata from resolve
      $rootScope.metadata.pageName = current.locals.metadata?.pageName || '';
      $rootScope.metadata.bodyClasses = current.locals.metadata?.bodyClasses || '';
      $rootScope.metadata.robots = current.locals.metadata?.robots || 'index';
      $rootScope.metadata.venueName = current.pathParams?.venueSlug || '';

      // Re-evaluate on every route change so the scheduled window opening or
      // closing is picked up even while the store is quiet.
      $rootScope.showMaintenance = shouldShowMaintenance();

      // analytics
      const newUrl = current?.originalPath;
      const oldUrl = previous?.originalPath;
      let referrer;

      if (newUrl !== oldUrl) {
        referrer = oldUrl;
      } else {
        ({ referrer } = $window.document);
      }

      const locationPath = $location.path();
      if (locationPath === '/global-dining-access') {
        try {
          utmLastTouch(Analytics, current.params);
        } catch (err) {
          Logger.error(err);
        }
      }

      try {
        if ([AMEX_GDA_PAGE, AMEX_RESY_CREDIT, AMEX_RESY_VALUE, AMEX_GDA_DIRECTORY_PAGE, AMEX_PLATINUM_NIGHTS_PAGE]
          .indexOf($rootScope.metadata?.pageName) > -1) {
          const isAuthenticated = selectIsAuthenticated(getState());
          GDACookieCleanup = setGDAPageCookie($location, isAuthenticated, Cookies, getState);
          let customArgs = null;
          if (isAuthenticated && getState()?.user?.userData) {
            customArgs = [
              $rootScope.metadata,
              $location,
              isAuthenticated,
              Cookies,
              selectIsGlobalDiningAccess(getState()),
              getState,
            ];
          } else if (!isAuthenticated) {
            customArgs = [
              $rootScope.metadata,
              $location,
              false,
              Cookies,
              false,
              getState,
            ];
          }
          if (customArgs) {
            const customData = getMktPageAnalytics(...customArgs);
            Analytics.marketingCustomPage(customData);
          }
        } else {
          Analytics.page($location, referrer, $rootScope.metadata, getState()?.user?.userData);
        }
      } catch (err) {
        Logger.error('Analytics.page error', err);
      }

      // Use case: clean up after resetSelectedCoreSeatsMinMax() is called when leaving VenuePage
      if (CORE_SEATS_PARAM in current.params) {
        if (thisSelectedCoreSeats && +current.params[CORE_SEATS_PARAM] !== thisSelectedCoreSeats) {
          setSearchParams(CORE_SEATS_PARAM, thisSelectedCoreSeats);
        }
      }
    });

    $rootScope.$on('$routeChangeError', () => $location.path('/'));

    // `reloadOnUrl: false` means the consolidated account route fires
    // `$routeUpdate` (not `$routeChangeSuccess`) on intra-route URL changes,
    // so we re-apply per-section title/metadata/analytics here and notify
    // React Router (which only listens for popstate) that the URL changed.
    $rootScope.$on('$routeUpdate', (e, current) => {
      if (current?.$$route?.originalPath !== ACCOUNT_ROUTE_PATH) return;

      $window.dispatchEvent(new $window.PopStateEvent('popstate'));

      const section = getAccountSectionFromPath($location.path());
      const meta = getAccountSectionMeta(section);

      $rootScope.metadata.pageName = meta.pageName || '';
      $rootScope.metadata.bodyClasses = meta.bodyClasses || '';
      $rootScope.metadata.robots = meta.robots || 'index';

      if (meta.titleKey) {
        $translate(`titles.${meta.titleKey}`)
          .then(
            (s) => { $rootScope.metadata.title = s; },
            () => { $rootScope.metadata.title = $rootScope.titleBase; }
          );
      } else {
        $rootScope.metadata.title = $rootScope.titleBase;
      }

      if (meta.descriptionKey) {
        $translate(`descriptions.${meta.descriptionKey}`)
          .then(
            (s) => { $rootScope.metadata.description = s; },
            () => { $rootScope.metadata.description = $rootScope.descriptionBase; }
          );
      } else {
        $rootScope.metadata.description = $rootScope.descriptionBase;
      }

      try {
        Analytics.page($location, $window.document.referrer, $rootScope.metadata, getState()?.user?.userData);
      } catch (err) {
        Logger.error('Analytics.page error', err);
      }

      $rootScope.$evalAsync(() => { $window.scrollTo(0, 0); });
    });

    $window.addEventListener('message', onPostMessage);

    function loadCollections() {
      const location = dispatch(getResyLocationsSelectedThunk());
      if (location?.code) {
        const key = `${location.code}-1-${selectIsAuthenticated(getState())}`;
        if (!Collections?.lists?.all[key]) {
          fetchCollections({
            location_id: location.code,
            include_algo: 1,
            isAuth: selectIsAuthenticated(getState()),
          });
        }
      }
    }

    function setTitle() {
      $translate('titles.default')
        .then((translationString) => {
          $rootScope.titleBase = translationString;
          $rootScope.metadata.title = translationString;
        });
      $translate('descriptions.default')
        .then((translationString) => {
          $rootScope.descriptionBase = translationString;
          $rootScope.metadata.description = translationString;
        });

      $rootScope.metadata.image_twitter = 'https://s3.amazonaws.com/resy.com/images/social/twitter-preview.png';
      $rootScope.metadata.image_facebook = 'https://s3.amazonaws.com/resy.com/images/social/facebook-preview.png';
    }

    function assessLanguage() {
      const isMobile = !$rootScope.matchMedia.md;
      const language = $translate.proposedLanguage() || $translate.use();

      const preferredLanguage = language || 'en-US';
      // used for tagging in mixpanel and simon
      window.preferredLanguage = preferredLanguage;
      $rootScope.metadata.language = preferredLanguage;

      let code = 'en';

      if (language) {
        if (language.includes('en')) {
          code = 'en';
          moment.locale('en', {
            calendar: {
              sameDay: '[Today]',
              nextDay: '[Tomorrow]',
              nextWeek: isMobile ? 'MMM. D' : 'ddd., MMM. D',
              sameElse: isMobile ? 'MMM. D' : 'ddd., MMM. D',
            },
          });
        } else if (language.includes('fr')) {
          code = 'fr';
          moment.locale('fr', {
            calendar: {
              sameDay: '[Aujourd’hui]',
              nextDay: '[Demain]',
              nextWeek: isMobile ? 'D MMM' : 'ddd D MMM',
              sameElse: isMobile ? 'D MMM' : 'ddd D MMM',
            },
          });
        } else if (language.includes('es')) {
          code = 'es';
          moment.locale('es', {
            calendar: {
              sameDay: '[hoy]',
              nextDay: '[mañana]',
              nextWeek: isMobile ? 'D MMM' : 'ddd, D MMM',
              sameElse: isMobile ? 'D MMM' : 'ddd, D MMM',
            },
          });
        } else if (language.includes('ja')) {
          code = 'ja';
          moment.locale('ja', {
            calendar: {
              sameDay: '[同日]',
              nextDay: '[翌日]',
              nextWeek: 'ddd, D MMM',
              sameElse: 'ddd, D MMM',
            },
          });
        } else if (language.includes('ca')) {
          code = 'ca';
          moment.locale('ca', {
            calendar: {
              sameDay: '[avui]',
              nextDay: '[demà]',
              nextWeek: isMobile ? 'D MMM' : 'ddd D MMM',
              sameElse: isMobile ? 'D MMM' : 'ddd D MMM',
            },
          });
        } else if (language.includes('nl')) {
          code = 'nl';
          moment.locale('nl', {
            calendar: {
              sameDay: '[Vandaag]',
              nextDay: '[Morgen]',
              nextWeek: isMobile ? 'D MMM' : 'ddd D MMM',
              sameElse: isMobile ? 'D MMM' : 'ddd D MMM',
            },
          });
        } else if (language.includes('pt')) {
          code = 'pt';
          moment.locale('pt', {
            calendar: {
              sameDay: '[Hoje]',
              nextDay: '[Amanhã]',
              nextWeek: isMobile ? 'D MMM' : 'ddd D MMM',
              sameElse: isMobile ? 'D MMM' : 'ddd D MMM',
            },
          });
        } else if (language.includes('de')) {
          code = 'de';
          moment.locale('de', {
            calendar: {
              sameDay: '[Heute]',
              nextDay: '[Morgen]',
              nextWeek: isMobile ? 'D MMM' : 'ddd D MMM',
              sameElse: isMobile ? 'D MMM' : 'ddd D MMM',
            },
          });
          // Fallback to en
        } else {
          moment.locale('en', {
            calendar: {
              sameDay: '[Today]',
              nextDay: '[Tomorrow]',
              nextWeek: isMobile ? 'MMM. D' : 'ddd., MMM. D',
              sameElse: isMobile ? 'MMM. D' : 'ddd., MMM. D',
            },
          });
        }
      }

      const html = $document[0].querySelector('html');
      html.setAttribute('lang', code);
      $window.resyLanguage = code; // eslint-disable-line
    }

    function assessBrowser() {
      const { userAgent } = $window.navigator;
      const browsers = {
        chrome: /chrome|crios/i, safari: /safari/i, firefox: /firefox/i, ie: /internet explorer/i,
      };

      Object.keys(browsers).forEach(
        (key) => {
          if (!$rootScope.browser) {
            if (browsers[key].test(userAgent)) {
              $rootScope.browser = key;
            }
          }
        }
      );
    }

    function onPostMessage(e) {
      if (!ALLOWED_DOMAINS.includes(e.origin) // functionally important
          || !e.data) { // a sanity-check
        return;
      }

      if (e.data.event === 'loginSync') {
        // Another context just logged in; whatever is cached or in flight may be for a different session
        resetUserFetchState();
        setAuthToken(e.data.token);
        dispatch(getUserThunk(true));
      } else if (e.data.event === 'logoutSync') {
        removeAuthToken();
        dispatch(userLogoutThunk());
      // For the 'onResyReservationsSync' case
      //
      // If widget deployment is lagging slightly behind consumer_web deployment, e.data will be a bare
      // string and e.data.affectsImmediateUserReservationCounts would then (not being on the String prototype)
      // be absent. If we encounter this, assume the true behavior for now.
      } else if (e.data === 'onResyReservationsSync' || e.data.event === 'onResyReservationsSync') {
        if (e.data.affectsImmediateUserReservationCounts ?? true) {
          dispatch(markReservationsChangedThunk());
        }
        // False case above: a concierge action, which doesn't change the logged-in user's own reservation count.
        dispatch(setShouldUserReservationsUpdate({ type: 'upcoming' }));
      } else if (e.data.event === 'onWindowOpenRequest') {
        // handles the opening external links from widget app
        // avoids cross origin issues with window.open
        window.open(e.data.url, e.data.target, e.data.features);
      } else if (e.data.event === 'authRedirect') {
        redirectToSanitizedUrl(e.data.redirectUrl);
      }
    }

    function setUpFlags() {
      const { sms } = $location.search();
      const showSmsFlow = Cookies.get('showSmsFlow');

      if (sms === 'true' && showSmsFlow !== 'true') {
        showSmsFlowCookieCleanup = setFunctionalCookie('showSmsFlow', true);
      }

      if (sms === 'false' && showSmsFlow !== 'false') {
        showSmsFlowCookieCleanup = setFunctionalCookie('showSmsFlow', false);
      }
    }

    function watchScroll() {
      $rootScope.$on('$routeChangeSuccess', (e, currentRoute) => {
        let shouldWatchScroll = false;
        // All routes EXCEPT - search page, static pages, account pages
        try {
          if (typeof currentRoute.locals.shouldNavWatchScroll === 'undefined') {
            shouldWatchScroll = true;
          } else {
            const bodyClasses = currentRoute.locals?.metadata?.bodyClasses || '';
            // account or static AND >= lg
            if ((bodyClasses.includes('page-account') || bodyClasses.includes('page-static')) && $rootScope.matchMedia.lg) {
              shouldWatchScroll = true;
            }
          }

          if (!isWatchingScroll && shouldWatchScroll) {
            $window.addEventListener('scroll', onScroll);
            isWatchingScroll = true;
          }
        } catch (err) {
          Logger.error(err);
        }
      });
    }

    function onScroll() {
      if ($window.scrollY <= 0) {
        $rootScope.isScrolled = false;
      } else {
        $rootScope.isScrolled = true;
      }
      $rootScope.$evalAsync();
    }

    function scrollTo(id) {
      $document[0].querySelector(`#${id}`).focus();
    }

    // Redirect to JCE logic and $locationChangeStart event handler
    const JCE_COOKIE_TTL_MS = 2 * 24 * 60 * 60 * 1000; // sets a 2 day TTL for the cookie, subject to change
    const JCE_RESERVATIONS_NOTIFY_PATH = '/account/reservations-and-notify';
    const JCE_ROUTE_MATCHERS = [
      new RegExp(`^${JCE_RESERVATIONS_NOTIFY_PATH}(?:/|$)`),
    ];

    const isJceRedirectRoute = (path) => JCE_ROUTE_MATCHERS.some((rx) => rx.test(path));

    const selectJceReservationNotifyEnabled = (state) => {
      const isExperimentsReady = state?.experiments?.loaded === true;
      if (!isExperimentsReady) return undefined;

      return state?.experiments?.[IS_JCE_RESERVATION_NOTIFY_ENABLED]?.enabled;
    };

    const isRedirectToJceEnabled = () => selectJceReservationNotifyEnabled(getState()) === true;

    const isJceCrossDomainHost = () => {
      const { jceRedirectDenyList } = getApiConfig('TockConfig') || {};
      const denyList = Array.isArray(jceRedirectDenyList) ? jceRedirectDenyList : [jceRedirectDenyList];
      return denyList.some((url) => {
        if (!url) return false;
        try {
          return new URL(url).origin === $window.location.origin;
        } catch {
          return false;
        }
      });
    };

    const shouldRedirectToJce = (nextPath) => {
      if (!isJceRedirectRoute(nextPath)) return false;

      if (!isRedirectToJceEnabled()) return false;

      if (isJceCrossDomainHost()) return false;

      return true;
    };

    const unsubscribeJceReservationNotifyFlag = observeStore(
      store,
      selectJceReservationNotifyEnabled,
      () => {}
    );

    $rootScope.$on('$destroy', () => {
      unsubscribeJceReservationNotifyFlag();
    });

    $rootScope.$on('$locationChangeStart', async (event, newUrl) => {
      const nextPath = new URL(newUrl, $window.location.origin).pathname;

      if (!shouldRedirectToJce(nextPath)) return;
      // cancel SPA route activation
      event.preventDefault();
      // set cookie for JCE (2-day TTL so a stale opt-in self-expires)
      await setEssentialCookieAsync(JCE_COOKIE_NAME, 'true', {
        expires: new Date(Date.now() + JCE_COOKIE_TTL_MS),
      });
      // trigger fresh browser request to same destination, now with cookie
      window.location.assign(nextPath);
    });

    $rootScope.$on('$routeChangeStart', (e, nextRoute) => {
      window.UCMCheckConsent = true;
      if (nextRoute.$$route.originalPath === '/cookie-policy') {
        window.UCMCheckConsent = false;
      }

      // eslint-disable-next-line no-param-reassign
      nextRoute.resolve = nextRoute.resolve || {};
      angular.extend(nextRoute.resolve, {
        config: () => getResyConfigThunk(),
      });
    });
  });
